/**
 * QueueFilters component.
 *
 * Secondary filters for queue views. Renders different filter controls based
 * on the primary filter:
 * - Active tabs: Tool, Agent, Blocking Only, Search
 * - History: Period, Operator, Outcome, Search
 */

import type { CSSProperties } from 'react'
import * as React from 'react'
import { Search, X } from 'lucide-react'
import { Input, Select, Toggle, Button } from '@/shared/components/ui'
import type { SelectOption } from '@/shared/components/ui'
import { useQueueStore, SEARCH_DEBOUNCE_MS } from '../store'
import type { AttentionItem, HistoryPeriod, ApprovalResolution } from '../types'

/**
 * Props for QueueFilters component.
 */
export interface QueueFiltersProps {
  /** Current queue filter (only 'history' is used with the new attention model). */
  filter: 'history'
  /** Current items to extract agent options from. */
  items: AttentionItem[]
  /** Optional list of available operators for history filter. */
  operators?: { id: string; name: string }[]
}

/**
 * Period options for history filter.
 */
const PERIOD_OPTIONS: SelectOption[] = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'all', label: 'All time' },
  { value: 'custom', label: 'Custom range' },
]

/**
 * Outcome options for history filter.
 */
const OUTCOME_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'All outcomes' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'suppressed', label: 'Suppressed' },
]

/**
 * Secondary filters for queue views.
 */
export function QueueFilters({ filter, items, operators }: QueueFiltersProps) {
  const historyFilters = useQueueStore((s) => s.historyFilters)
  const setHistoryFilters = useQueueStore((s) => s.setHistoryFilters)
  const clearHistoryFilters = useQueueStore((s) => s.clearHistoryFilters)
  // Alias for backward compatibility within this component
  const filters = historyFilters
  const setFilters = setHistoryFilters
  const clearFilters = clearHistoryFilters
  const [searchInput, setSearchInput] = React.useState(filters.search ?? '')
  const debounceTimer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  // Extract unique attention types from history items for the type filter
  const typeOptions = React.useMemo(() => {
    const types = new Set<string>()
    for (const item of items) {
      if (item.attention_type) {
        types.add(item.attention_type)
      }
    }
    const typeLabels: Record<string, string> = {
      workflow_step_approval: 'Step Approval',
      agent_tool_approval: 'Tool Approval',
      workflow_agent_approval: 'Workflow Agent',
      workflow_human_review: 'Human Review',
      agent_text_review: 'Agent Text Review',
      pr_review: 'PR Review',
    }
    return [
      { value: '', label: 'All Types' },
      ...Array.from(types).sort().map((t) => ({ value: t, label: typeLabels[t] ?? t })),
    ]
  }, [items])

  const agentOptions = React.useMemo(() => {
    const agents = new Set<string>()
    for (const item of items) {
      if (item.agent_id) {
        agents.add(item.agent_id)
      }
    }
    return [
      { value: '', label: 'All Agents' },
      ...Array.from(agents).sort().map((a) => ({ value: a, label: a })),
    ]
  }, [items])

  const operatorOptions = React.useMemo(() => {
    const ops = operators ?? []
    return [
      { value: '', label: 'All Operators' },
      ...ops.map((o) => ({ value: o.id, label: o.name })),
    ]
  }, [operators])

  // Debounced search
  React.useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }
    debounceTimer.current = setTimeout(() => {
      if (searchInput !== (filters.search ?? '')) {
        setFilters({ search: searchInput || undefined })
      }
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
    }
  }, [searchInput, filters.search, setFilters])

  // Sync search input with store state
  React.useEffect(() => {
    setSearchInput(filters.search ?? '')
  }, [filters.search])

  const hasActiveFilters = React.useMemo(() => {
    return !!(
      filters.tool ||
      filters.agent ||
      filters.blockingOnly ||
      filters.search ||
      filters.operator ||
      (filters.outcome && filters.outcome !== 'all') ||
      (filters.period && filters.period !== '7d')
    )
  }, [filters])

  const isHistory = filter === 'history'

  return (
    <div style={styles.container}>
      {/* Type Filter */}
      <Select
        value={filters.tool ?? ''}
        onChange={(value) => setFilters({ tool: value || undefined })}
        options={typeOptions}
        style={styles.select}
        aria-label="Filter by type"
      />

      {/* Agent Filter */}
      <Select
        value={filters.agent ?? ''}
        onChange={(value) => setFilters({ agent: value || undefined })}
        options={agentOptions}
        style={styles.select}
        aria-label="Filter by agent"
      />

      {/* History-specific filters */}
      {isHistory ? (
        <>
          <Select
            value={filters.period ?? '7d'}
            onChange={(value) => setFilters({ period: value as HistoryPeriod })}
            options={PERIOD_OPTIONS}
            style={styles.selectNarrow}
            aria-label="Filter by time period"
          />

          <Select
            value={filters.operator ?? ''}
            onChange={(value) => setFilters({ operator: value || undefined })}
            options={operatorOptions}
            style={styles.select}
            aria-label="Filter by operator"
          />

          <Select
            value={filters.outcome ?? 'all'}
            onChange={(value) => setFilters({ outcome: value as ApprovalResolution | 'all' })}
            options={OUTCOME_OPTIONS}
            style={styles.selectNarrow}
            aria-label="Filter by outcome"
          />
        </>
      ) : (
        /* Blocking Only toggle for non-history tabs */
        <Toggle
          checked={filters.blockingOnly ?? false}
          onCheckedChange={(checked) => setFilters({ blockingOnly: checked })}
          label="Blocking Only"
          aria-label="Show only blocking items"
        />
      )}

      {/* Search */}
      <div style={styles.searchWrapper}>
        <Search style={styles.searchIcon} />
        <Input
          type="search"
          placeholder="Search..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          style={styles.searchInput}
          aria-label="Search queue items"
        />
        {searchInput && (
          <button
            type="button"
            onClick={() => {
              setSearchInput('')
              setFilters({ search: undefined })
            }}
            style={styles.clearSearchButton}
            className="queue-filter-clear"
            aria-label="Clear search"
          >
            <X style={styles.clearIcon} />
          </button>
        )}
      </div>

      {/* Clear all filters button */}
      {hasActiveFilters && (
        <Button variant="ghost" size="sm" onClick={clearFilters} style={styles.clearButton}>
          <X style={styles.clearButtonIcon} />
          Clear filters
        </Button>
      )}
    </div>
  )
}

const styles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '12px',
  },
  select: {
    width: '160px',
  },
  selectNarrow: {
    width: '144px',
  },
  searchWrapper: {
    position: 'relative',
    flex: 1,
    minWidth: '200px',
    maxWidth: '320px',
  },
  searchIcon: {
    position: 'absolute',
    left: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '16px',
    height: '16px',
    color: 'var(--text-muted)',
  },
  searchInput: {
    paddingLeft: '32px',
    paddingRight: '32px',
  },
  clearSearchButton: {
    position: 'absolute',
    right: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    padding: 0,
    color: 'var(--text-muted)',
    cursor: 'pointer',
  },
  clearIcon: {
    width: '16px',
    height: '16px',
  },
  clearButton: {
    color: 'var(--text-muted)',
  },
  clearButtonIcon: {
    width: '12px',
    height: '12px',
    marginRight: '4px',
  },
}

export default QueueFilters
